/**
 * Profile Page Component
 * 
 * Main page for managing user profile settings
 * Includes manual save, password change, data export, and account deletion
 * 
 * Features:
 * - Profile display with avatar
 * - Manual save form for profile fields
 * - Change password modal
 * - Export data modal
 * - Delete account modal
 * - Image upload functionality
 * - WCAG 2.2 AA compliant
 * 
 * @see .specify/memory/constitution.md
 * @see specs/004-settings-profile-png/spec.md
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { 
  User, 
  Lock, 
  Download, 
  Trash2,
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Monitor,
  Settings,
  ChevronDown
} from 'lucide-react';
import { Avatar } from '../../components/ui/Avatar';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { DeleteAccountModal } from './components/DeleteAccountModal';
import { ExportDataModal } from './components/ExportDataModal';
import { profileService } from '../../services/profile-service';
import { profileImageService } from '../../services/profile-image-service';
import { supabaseAuthService } from '../../services/supabase-service';
import { formatPhoneNumber } from '../../utils/phone-format';
import { setNavigationGuardState } from './useNavigationGuardState';
import { PageHeader } from '../../components/PageHeader';
import { useDemoMode } from '../../hooks/useDemoMode';
import type { 
  UpdateProfileInput, 
  ChangePasswordInput,
  ChangeEmailInput,
  DeleteAccountInput,
  ExportDataOptions
} from '../../types/profile.types';

export function ProfilePage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateProfileInput>({});
  const [emailFormData, setEmailFormData] = useState<ChangeEmailInput>({ newEmail: '' });
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showExportDataModal, setShowExportDataModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Fetch profile data
  const { data: profileResponse, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const result = await profileService.getProfile();
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    retry: 1,
  });

  const profile = profileResponse;

  // Check if user is in demo mode
  const { isDemoMode } = useDemoMode();

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phoneNumber: profile.phoneNumber || '',
        dateOfBirth: profile.dateOfBirth || '',
        countryOfResidence: profile.countryOfResidence || 'Australia',
      });
      setEmailFormData({ newEmail: profile.email || '' });
      setIsDirty(false);
      setHasUnsavedChanges(false);
    }
  }, [profile]);

  // Track dirty state
  const dirtyState = useMemo(() => {
    if (!profile) return false;

    return (
      formData.firstName !== (profile.firstName || '') ||
      formData.lastName !== (profile.lastName || '') ||
      formData.phoneNumber !== (profile.phoneNumber || '') ||
      formData.dateOfBirth !== (profile.dateOfBirth || '') ||
      formData.countryOfResidence !== (profile.countryOfResidence || 'Australia')
    );
  }, [formData, profile]);

  // Update dirty state when computed value changes
  useEffect(() => {
    setIsDirty(dirtyState);
    setHasUnsavedChanges(dirtyState);
  }, [dirtyState]);

  // Navigation guard for unsaved changes
  const previousLocationRef = useRef(location.pathname);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    if (previousLocationRef.current !== location.pathname && hasUnsavedChanges) {
      window.history.pushState(null, '', previousLocationRef.current);
      setPendingNavigation(location.pathname);
      setShowNavigationDialog(true);
    }

    previousLocationRef.current = location.pathname;
  }, [location.pathname, hasUnsavedChanges]);

  useEffect(() => {
    const handlePopState = () => {
      if (hasUnsavedChanges) {
        window.history.pushState(null, '', location.pathname);
        setPendingNavigation(previousLocationRef.current);
        setShowNavigationDialog(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges, location.pathname]);

  const handleNavigationAttempt = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowNavigationDialog(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  // Register guard state with SettingsLayout
  useEffect(() => {
    setNavigationGuardState(hasUnsavedChanges, handleNavigationAttempt);
    return () => {
      setNavigationGuardState(false, null);
    };
  }, [hasUnsavedChanges, handleNavigationAttempt]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (input: UpdateProfileInput) => {
      const result = await profileService.updateProfile(input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setSaveStatus('saved');
      setIsDirty(false);
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
    onError: (error: Error) => {
      setSaveStatus('error');
      setErrorMessage(error.message);
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
    },
  });

  // Handle form field changes
  const handleFieldChange = (field: keyof UpdateProfileInput) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value = e.target.value;
    
    // Auto-format phone number as user types
    if (field === 'phoneNumber') {
      value = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setSaveStatus('idle');
  };

  // Save handler
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!isDirty) return true;

    setSaveStatus('saving');
    setErrorMessage('');

    try {
      // Filter out locked fields in demo mode (defense in depth)
      const payload = isDemoMode
        ? { ...formData, firstName: undefined, lastName: undefined }
        : formData;
      
      await updateProfileMutation.mutateAsync(payload);
      setSaveStatus('saved');
      setIsDirty(false);
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
      return true;
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to save profile');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
      return false;
    }
  }, [isDirty, formData, updateProfileMutation, isDemoMode]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  // Navigation dialog handlers
  const handleNavigationDialogSave = useCallback(async () => {
    const saveSuccessful = await handleSave();
    if (saveSuccessful) {
      // Close dialog and clear pending navigation, but stay on current page
      setShowNavigationDialog(false);
      setPendingNavigation(null);
      // Don't navigate - user stays on page to see the success message
    }
  }, [handleSave]);

  const handleNavigationDialogDiscard = useCallback(() => {
    setShowNavigationDialog(false);
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phoneNumber: profile.phoneNumber || '',
        dateOfBirth: profile.dateOfBirth || '',
        countryOfResidence: profile.countryOfResidence || 'Australia',
      });
      setIsDirty(false);
      setHasUnsavedChanges(false);
    }
    if (pendingNavigation) {
      isNavigatingRef.current = true;
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [profile, pendingNavigation, navigate]);

  const handleNavigationDialogCancel = useCallback(() => {
    setShowNavigationDialog(false);
    setPendingNavigation(null);
  }, []);

  // Image upload handler
  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    setErrorMessage(''); // Clear any previous errors
    try {
      console.log('Starting image upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      const result = await profileImageService.uploadImage(file);
      
      if (result.error) {
        console.error('Image upload error:', result.error);
        const errorMsg = result.error.code === 'PERMISSION_ERROR' 
          ? 'Permission denied. Please check that the storage bucket exists and RLS policies are configured.'
          : result.error.code === 'AUTH_ERROR'
          ? 'You must be logged in to upload images.'
          : result.error.message || 'Failed to upload image';
        
        setErrorMessage(errorMsg);
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus('idle');
          setErrorMessage('');
        }, 8000); // Show error longer for debugging
      } else {
        console.log('Image uploaded successfully:', result.data);
        setSaveStatus('saved');
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error: any) {
      console.error('Unexpected error during image upload:', error);
      setErrorMessage(error.message || 'Failed to upload image');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 8000);
    } finally {
      setUploadingImage(false);
    }
  };

  // Image remove handler
  const handleImageRemove = async () => {
    setUploadingImage(true);
    try {
      const result = await profileImageService.deleteImage();
      if (result.error) {
        setErrorMessage(result.error.message);
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus('idle');
          setErrorMessage('');
        }, 5000);
      } else {
        queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to remove image');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
    } finally {
      setUploadingImage(false);
    }
  };

  // Change password handler
  const handleChangePassword = async (input: ChangePasswordInput) => {
    try {
      const result = await supabaseAuthService.changePassword(input);
      if (result.error) {
        setErrorMessage(result.error.message);
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus('idle');
          setErrorMessage('');
        }, 5000);
      } else {
        setShowChangePasswordModal(false);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to change password');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  // Change email handler
  const handleChangeEmail = async (input: ChangeEmailInput) => {
    try {
      const result = await supabaseAuthService.changeEmail(input);
      if (result.error) {
        setErrorMessage(result.error?.message || 'Failed to change email');
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus('idle');
          setErrorMessage('');
        }, 5000);
      } else {
        setShowChangeEmailModal(false);
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to change email');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  // Export data handler
  const handleExportData = async (options: ExportDataOptions) => {
    try {
      const result = await profileService.exportData(options);
      if (result.error) {
        setErrorMessage(result.error.message);
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus('idle');
          setErrorMessage('');
        }, 5000);
      } else {
        // Service already triggers download
        setShowExportDataModal(false);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to export data');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  // Delete account handler
  const handleDeleteAccount = async (input: DeleteAccountInput) => {
    try {
      const result = await profileService.deleteAccount(input);
      if (result.error) {
        setErrorMessage(result.error.message);
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus('idle');
          setErrorMessage('');
        }, 5000);
      } else {
        // Redirect to login page
        window.location.href = '/login';
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to delete account');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };


  if (isLoadingProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Profile Settings"
          description="Manage your personal information and account details"
          helpSlug="settings-profile"
          headingLevel="h2"
        />
        <div className="card">
          <div className="card-content">
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Failed to load profile</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Profile Settings"
        description="Manage your personal information and account details"
        helpSlug="settings-profile"
        headingLevel="h2"
      />

      {/* Save Status */}
      {(saveStatus !== 'idle' || errorMessage) && (
        <div className={`rounded-lg p-4 ${
          saveStatus === 'saved' 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : saveStatus === 'error' || errorMessage
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
        }`}>
          <div className="flex items-center space-x-2">
            {saveStatus === 'saved' && <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />}
            {saveStatus === 'error' && <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
            {saveStatus === 'saving' && <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" />}
            <p className={`text-sm font-medium ${
              saveStatus === 'saved'
                ? 'text-green-800 dark:text-green-200'
                : saveStatus === 'error' || errorMessage
                ? 'text-red-800 dark:text-red-200'
                : 'text-blue-800 dark:text-blue-200'
            }`}>
              {saveStatus === 'saved' && 'Profile updated successfully'}
              {saveStatus === 'saving' && 'Saving changes...'}
              {(saveStatus === 'error' || errorMessage) && (errorMessage || 'Failed to save changes')}
            </p>
          </div>
        </div>
      )}

      {/* Profile Form - Combined with Profile Summary */}
      <form onSubmit={handleFormSubmit} className="card">
        <div className="px-6 pt-6 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Personal Information
            </h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage your personal details and profile image
          </p>
        </div>
        <div className="card-content space-y-6">
          {/* Profile Summary Section */}
          <div className="flex flex-col items-center sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-shrink-0">
              <Avatar
                profile={profile}
                size="xl"
                showUpload={!uploadingImage && !isDemoMode}
                onUpload={isDemoMode ? undefined : handleImageUpload}
                onRemove={isDemoMode ? undefined : handleImageRemove}
              />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {profile.firstName && profile.lastName
                  ? `${profile.firstName} ${profile.lastName}`
                  : profile.email}
              </h2>
              <p className="text-base text-gray-600 dark:text-gray-400 mt-1">
                {profile.email}
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  profile.accountStatus === 'active'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {profile.accountStatus === 'active' ? 'Active Account' : 'Inactive Account'}
                </span>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  profile.membershipType === 'premium'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : profile.membershipType === 'demo'
                    ? 'bg-[#FAEBD7] text-[#A52A2A] dark:bg-orange-900/30 dark:text-orange-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {profile.membershipType === 'premium' ? 'Premium Member' : profile.membershipType === 'demo' ? 'Demo Member' : 'Free Member'}
                </span>
                {profile.role === 'admin' && (
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    Admin
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Form Fields Section */}
          {/* Two-column grid for form fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName || ''}
                onChange={handleFieldChange('firstName')}
                disabled={isDemoMode}
                className={`input w-full ${isDemoMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                placeholder="Enter your first name"
                title={isDemoMode ? 'Demo account - name cannot be modified' : undefined}
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName || ''}
                onChange={handleFieldChange('lastName')}
                disabled={isDemoMode}
                className={`input w-full ${isDemoMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                placeholder="Enter your last name"
                title={isDemoMode ? 'Demo account - name cannot be modified' : undefined}
              />
            </div>

            {/* Email - Full Width */}
            <div className="md:col-span-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="email"
                  id="email"
                  value={emailFormData.newEmail}
                  onChange={(e) => setEmailFormData({ newEmail: e.target.value })}
                  className={`input flex-1 ${isDemoMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                  placeholder="Enter your email"
                  disabled={!profile.emailConfirmed || isDemoMode}
                  title={isDemoMode ? 'Demo account - email cannot be modified' : undefined}
                />
                {profile.emailConfirmed && !isDemoMode && (
                  <button
                    type="button"
                    onClick={() => setShowChangeEmailModal(true)}
                    className="btn-secondary btn-md whitespace-nowrap"
                  >
                    Change Email
                  </button>
                )}
              </div>
              {!profile.emailConfirmed && !isDemoMode && (
                <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                  Please verify your email address before changing it
                </p>
              )}
              {isDemoMode && (
                <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                  Demo account - email cannot be modified
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber || ''}
                onChange={handleFieldChange('phoneNumber')}
                className="input w-full"
                placeholder="04XX XXX XXX"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Australian format: 04XX XXX XXX
              </p>
            </div>

            {/* Date of Birth */}
            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date of birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                value={formData.dateOfBirth || ''}
                onChange={handleFieldChange('dateOfBirth')}
                className="input w-full"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Country of Residence - 50% width on desktop */}
            <div>
              <label htmlFor="countryOfResidence" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country of residence
              </label>
              <div className="relative">
                <select
                  id="countryOfResidence"
                  value={formData.countryOfResidence || 'Australia'}
                  onChange={handleFieldChange('countryOfResidence')}
                  className="input w-full appearance-none pr-10 cursor-pointer"
                >
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                </select>
                <ChevronDown 
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" 
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>

          {/* Save Changes Button */}
          <div className="flex justify-end pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              className="btn-primary btn-md"
              disabled={!isDirty || saveStatus === 'saving'}
              data-loading={saveStatus === 'saving' ? 'true' : undefined}
            >
              <Save className="h-4 w-4 mr-2" aria-hidden="true" />
              {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>

      {/* Account Actions */}
      {!isDemoMode && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Account Actions
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage your account settings and data
            </p>
          </div>

          <div className="space-y-0">
            {/* Change Password */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Password</div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {profile.passwordLastChanged 
                    ? `Last changed ${new Date(profile.passwordLastChanged).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}`
                    : 'Never changed'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(true)}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
              >
                Change Password
              </button>
            </div>

            {/* Login Sessions */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Monitor className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Login Sessions</div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Manage active sessions across devices
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/settings/security')}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm flex items-center space-x-1"
              >
                <span>View Sessions</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            {/* Export All Data */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Download className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Export All Data</div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Download your data in JSON or CSV format
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowExportDataModal(true)}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
              >
                Export Data
              </button>
            </div>

            {/* Delete Account */}
            <div className="flex items-center justify-between py-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Trash2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Delete Account</div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Permanently delete your account and all data (30-day recovery window)
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteAccountModal(true)}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium text-sm"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Guard Dialog */}
      {showNavigationDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="navigation-dialog-title"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 id="navigation-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                You have unsaved changes. What would you like to do?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleNavigationDialogCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNavigationDialogDiscard}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Discard Changes
                </button>
                <button
                  type="button"
                  onClick={handleNavigationDialogSave}
                  disabled={saveStatus === 'saving'}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSubmit={handleChangePassword}
        loading={saveStatus === 'saving'}
      />

      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onSubmit={handleDeleteAccount}
        loading={saveStatus === 'saving'}
      />

      <ExportDataModal
        isOpen={showExportDataModal}
        onClose={() => setShowExportDataModal(false)}
        onSubmit={handleExportData}
        loading={saveStatus === 'saving'}
      />

      {/* Change Email Modal (simplified inline for now) */}
      {showChangeEmailModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowChangeEmailModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-900">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h2 className="text-lg font-semibold">Change Email</h2>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  A verification email will be sent to your new email address. Your current email will remain active until verification is complete.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowChangeEmailModal(false)}
                    className="btn-secondary btn-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChangeEmail(emailFormData)}
                    className="btn-primary btn-md"
                  >
                    Send Verification Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

